import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/calcEngine";
import { formatCurrency, isoDate } from "@/lib/format";
import {
  Calculator, FileBarChart, Share2, ShieldCheck, Smartphone, WifiOff,
  ChevronRight, Sparkles, Leaf, ArrowRight, Check,
} from "lucide-react";
import StateRequestSection from "@/components/StateRequestSection";

const CAT_META = {
  balvatika: { color: "#DB2777", emoji: "🧒" },
  primary: { color: "#2563EB", emoji: "🎒" },
  upper_primary: { color: "#059669", emoji: "📘" },
  high_school: { color: "#EA580C", emoji: "🎓" },
};

export default function Home() {
  const { t } = useLang();
  const [counts, setCounts] = useState({ balvatika: 12, primary: 48, upper_primary: 35, high_school: 20 });
  const [result, setResult] = useState(null);
  const [price, setPrice] = useState(99);

  useEffect(() => { api.get("/payments/config").then(({ data }) => setPrice(data.price)).catch(() => {}); }, []);
  useEffect(() => {
    const id = setTimeout(() => {
      api.post("/calc/demo", { date: isoDate(new Date()), students: counts }).then(({ data }) => setResult(data)).catch(() => {});
    }, 400);
    return () => clearTimeout(id);
  }, [counts]);

  const features = [
    { icon: Calculator, title: t("mdm_calc"), desc: "Exact per-student cooking cost with auto-balanced vegetables.", color: "bg-cat-balvatika" },
    { icon: FileBarChart, title: t("reports"), desc: "Daily, Weekly, Monthly (MDCF), Annual & UC reports.", color: "bg-cat-primary" },
    { icon: Share2, title: t("share_whatsapp"), desc: "Download PDF / Excel and share on WhatsApp instantly.", color: "bg-cat-upper" },
    { icon: WifiOff, title: "Works Offline", desc: "Add to home screen. Calculator works without internet.", color: "bg-cat-high" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-6 md:pt-16 md:grid md:grid-cols-2 md:gap-10 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-thali-green/10 text-thali-green px-3 py-1 text-sm font-bold mb-4">
              <Leaf className="w-4 h-4" /> Odisha PM POSHAN (Mid Day Meal)
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-thali-ink leading-tight">
              {t("cooking_cost")} <span className="text-thali-green">+ {t("rice_calc")}</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-thali-ink/70 max-w-lg">{t("tagline")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" className="btn-3d-saffron" data-testid="hero-register-btn">
                {t("get_started")} <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/login" className="btn-ghost" data-testid="hero-login-btn">{t("login")}</Link>
            </div>
            <div className="mt-5 flex items-center gap-4 text-sm text-thali-ink/60">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-thali-green" /> Secure</span>
              <span className="inline-flex items-center gap-1"><Smartphone className="w-4 h-4 text-thali-green" /> Mobile-first</span>
              <span className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4 text-thali-green" /> Free</span>
            </div>
          </div>

          {/* Live demo calculator */}
          <div className="mt-8 md:mt-0 card-thali animate-fade-in-up" style={{ animationDelay: "120ms" }} data-testid="home-demo-calc">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg text-thali-green">Try it now</h3>
              <span className="text-xs bg-thali-turmeric/30 rounded-full px-2 py-1 font-semibold">Live demo</span>
            </div>
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <div key={c} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-thali-turmeric/30 p-2 pl-3"
                  style={{ borderLeft: `5px solid ${CAT_META[c].color}` }}>
                  <span className="text-sm font-semibold text-thali-ink flex items-center gap-2">
                    <span>{CAT_META[c].emoji}</span> {t(c)}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCounts((p) => ({ ...p, [c]: Math.max(0, p[c] - 1) }))}
                      className="w-9 h-9 rounded-lg bg-thali-cream font-bold text-lg" aria-label="decrease">−</button>
                    <input value={counts[c]} inputMode="numeric" pattern="[0-9]*"
                      onChange={(e) => setCounts((p) => ({ ...p, [c]: parseInt(e.target.value || 0, 10) || 0 }))}
                      className="w-12 text-center font-num font-bold text-lg bg-transparent" data-testid={`demo-input-${c}`} />
                    <button onClick={() => setCounts((p) => ({ ...p, [c]: p[c] + 1 }))}
                      className="w-9 h-9 rounded-lg bg-thali-green text-white font-bold text-lg" aria-label="increase">+</button>
                  </div>
                </div>
              ))}
            </div>
            {result && (
              <div className="mt-4 rounded-xl bg-thali-green text-white p-4 flex items-center justify-between" data-testid="demo-result">
                <div>
                  <div className="text-xs opacity-80">{t("grand_total")} · {result.summary.total_students} {t("students")}</div>
                  <div className="text-3xl font-num font-extrabold">{formatCurrency(result.summary.grand_total)}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="opacity-90" data-testid="demo-rice">{result.summary.total_rice_kg} kg rice</div>
                </div>
              </div>
            )}
            {result && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="demo-quantities">
                {Object.entries(result.aggregated_items || {}).map(([n, it]) => (
                  <span key={n} className="text-xs bg-white border border-thali-turmeric/30 rounded-full px-2 py-1">
                    {it.emoji} {n === "Condiment & Salt" ? "Masala & Salt" : n}: {it.unit === "g" && it.total_quantity >= 1000 ? `${(it.total_quantity / 1000).toFixed(2)} kg` : `${Math.round(it.total_quantity * 100) / 100} ${it.unit}`}
                  </span>
                ))}
              </div>
            )}
            <Link to="/register" className="btn-3d-green w-full mt-3" data-testid="demo-cta">{t("save_entry")} <ChevronRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-10 md:py-16">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-thali-ink text-center mb-8">Everything an MDM In-charge needs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div key={i} className="card-thali hover:-translate-y-1 transition-transform" data-testid={`feature-${i}`}>
              <div className={`w-12 h-12 rounded-xl ${f.color} text-white grid place-items-center mb-3`}><f.icon className="w-6 h-6" /></div>
              <h3 className="font-heading font-bold text-lg mb-1">{f.title}</h3>
              <p className="text-sm text-thali-ink/70">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <StateRequestSection />

      {/* How it works */}
      <section className="bg-thali-paper border-y border-thali-turmeric/20">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-center mb-10">How it works — 3 simple steps</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: 1, t: "Enter students", d: "Pick a date and enter present students per class using easy +/− steppers.", c: "#DB2777" },
              { n: 2, t: "Calculate & save", d: "Get exact cooking cost, rice kg and item-wise breakdown. Save the day's entry.", c: "#2563EB" },
              { n: 3, t: "Generate reports", d: "Produce Daily, MDCF, Annual & UC reports. Download PDF/Excel or share on WhatsApp.", c: "#EA580C" },
            ].map((s) => (
              <div key={s.n} className="relative card-thali pt-8">
                <span className="absolute -top-5 left-5 w-11 h-11 rounded-full text-white font-num font-extrabold text-xl grid place-items-center shadow-lg" style={{ background: s.c }}>{s.n}</span>
                <h3 className="font-heading font-bold text-lg mb-1">{s.t}</h3>
                <p className="text-sm text-thali-ink/70">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-12" data-testid="home-pricing">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-thali-green/10 text-thali-green px-3 py-1 text-sm font-bold mb-2"><Sparkles className="w-4 h-4" /> {t("pricing")}</span>
          <h2 className="text-2xl md:text-3xl font-heading font-bold">One simple plan</h2>
          <p className="text-thali-ink/70 mt-2">Public pages and the live demo are free. Saving, reports & downloads need the plan.</p>
        </div>
        <div className="card-thali max-w-sm mx-auto overflow-hidden p-0">
          <div className="bg-thali-green text-white p-6 text-center">
            <div className="text-sm opacity-80">{t("annual_plan")}</div>
            <div className="text-5xl font-num font-extrabold mt-1">₹{price}<span className="text-lg font-semibold opacity-80">/{t("year")}</span></div>
            <div className="text-xs opacity-80 mt-1">{t("incl_taxes")} · 365 days</div>
          </div>
          <div className="p-6">
            <ul className="space-y-2">
              {["Save daily entries & history", "Daily, Weekly, Monthly (MDCF), Annual & UC reports", "PDF & Excel downloads", "Share on WhatsApp"].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-thali-green mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/pricing" className="btn-3d-green w-full mt-5" data-testid="home-pricing-cta">{t("get_started")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold mb-3">Start calculating in under a minute</h2>
        <p className="text-thali-ink/70 mb-6">Public tools and the live demo are free. Save entries & generate reports with the Annual Plan.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="btn-3d-saffron" data-testid="bottom-cta">{t("get_started")} <ArrowRight className="w-5 h-5" /></Link>
          <Link to="/pricing" className="btn-ghost" data-testid="bottom-pricing">{t("pricing")}</Link>
        </div>
      </section>
    </div>
  );
}
